/**
 * DailyCheckIn — Compact summary/fallback widget
 * Shows progress ring + stats when RoutineAdventure is the primary experience.
 * Used on settings page and as "already completed today" summary.
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useChildStore, useSoundEffects, useHaptic } from '../hooks';
import { kidPalette } from '../styles/kidPalette';

/** SVG progress ring with kid colors */
function ProgressRing({ completed, total }: { completed: number; total: number }) {
  const size = 48;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? completed / total : 0;
  const offset = circumference * (1 - progress);

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={kidPalette.border}
          strokeWidth={strokeWidth}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={progress >= 1 ? kidPalette.meadow : kidPalette.ocean}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-kid-xs font-display text-kid-text">
          {completed}/{total}
        </span>
      </div>
    </div>
  );
}

export default function DailyCheckIn() {
  const { selectedChild, habits, streakData, fetchHabits, toggleHabit } = useChildStore();
  const { play } = useSoundEffects();
  const haptic = useHaptic();
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (selectedChild?.id) {
      fetchHabits(selectedChild.id);
    }
  }, [selectedChild?.id, fetchHabits]);

  const handleToggle = useCallback(
    (habitId: string, currentlyCompleted: boolean) => {
      if (selectedChild?.id) {
        toggleHabit(selectedChild.id, habitId, !currentlyCompleted);
        play(currentlyCompleted ? 'tap' : 'taskChime');
        haptic.tap();
      }
    },
    [selectedChild?.id, toggleHabit, play, haptic]
  );

  const completedCount = habits.filter((h) => h.completedToday).length;
  const allDone = completedCount === habits.length && habits.length > 0;

  // Auto-collapse when all done
  useEffect(() => {
    if (allDone) {
      const timer = setTimeout(() => setIsCollapsed(true), 800);
      return () => clearTimeout(timer);
    }
  }, [allDone]);

  if (!habits.length) return null;

  return (
    <div
      className={`overflow-hidden transition-colors rounded-kid ${
        allDone ? 'bg-kid-meadow/5' : ''
      }`}
    >
      {/* Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-kid-bg/50 transition-colors touch-target"
      >
        <div className="flex items-center gap-3">
          <ProgressRing completed={completedCount} total={habits.length} />
          <div className="text-left">
            <h2 className="font-display text-kid-base text-kid-text flex items-center gap-2">
              Daily Check-In
              {allDone && (
                <span className="text-[11px] font-kid font-bold text-kid-meadow bg-kid-meadow/10 px-2 py-0.5 rounded-full">
                  All done!
                </span>
              )}
            </h2>
            <p className="text-kid-xs text-kid-text-soft font-kid">
              {allDone
                ? 'Great job completing all your habits!'
                : `${habits.length - completedCount} of ${habits.length} remaining`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {streakData && streakData.habitStreak > 0 && (
            <span className="inline-flex items-center gap-1 bg-kid-tangerine/10 text-kid-tangerine text-kid-xs font-display px-2 sm:px-2.5 py-1 rounded-full">
              🔥 {streakData.habitStreak}
            </span>
          )}
          {streakData && (
            <span className="inline-flex items-center gap-1 bg-kid-dandelion/10 text-kid-dandelion text-kid-xs font-display px-2 sm:px-2.5 py-1 rounded-full">
              ⭐ {streakData.habitPoints}
            </span>
          )}
          <motion.div
            animate={{ rotate: isCollapsed ? 0 : 180 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="w-5 h-5 text-kid-text-soft" />
          </motion.div>
        </div>
      </button>

      {/* Collapsible content */}
      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 sm:px-5 pb-4 sm:pb-5">
              <div className="space-y-2">
                {habits.map((habit, i) => (
                  <motion.div
                    key={habit.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                    className={`flex items-center justify-between p-3 rounded-kid-sm transition-colors ${
                      habit.completedToday ? 'bg-kid-meadow/10' : 'bg-kid-bg'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{habit.emoji}</span>
                      <span
                        className={`font-kid text-kid-sm font-medium ${
                          habit.completedToday ? 'text-kid-meadow line-through' : 'text-kid-text'
                        }`}
                      >
                        {habit.name}
                      </span>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggle(habit.id, habit.completedToday);
                      }}
                      className={`relative w-11 h-6 rounded-full transition-colors touch-target ${
                        habit.completedToday ? 'bg-kid-meadow' : 'bg-gray-300'
                      }`}
                    >
                      <motion.div
                        className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md"
                        animate={{ x: habit.completedToday ? 20 : 0 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    </button>
                  </motion.div>
                ))}
              </div>

              {allDone && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mt-3 text-center py-2.5 bg-kid-meadow/10 rounded-kid-sm border-2 border-kid-meadow/20"
                >
                  <span className="text-kid-meadow font-display text-kid-sm">
                    🎉 All done today! Keep it up!
                  </span>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
